'use client';

import { sanitizeURL } from '@/utils/domain/route';
/* eslint-disable no-restricted-imports */
import { useRouter } from 'next/navigation';

interface NavigateOptions {
  scroll?: boolean;
}

enum PrefetchKind {
  AUTO = 'auto',
  FULL = 'full',
  TEMPORARY = 'temporary',
}
interface PrefetchOptions {
  kind: PrefetchKind;
}

type AppRouterInstance = {
  /**
   * Navigate to the previous history entry.
   */
  back(): void;
  /**
   * Navigate to the next history entry.
   */
  forward(): void;
  /**
   * Refresh the current page.
   */
  refresh(): void;
  /**
   * Navigate to the provided href.
   * Pushes a new history entry.
   */
  push(href: string, options?: NavigateOptions): void;
  /**
   * Navigate to the provided href.
   * Replaces the current history entry.
   */
  replace(href: string, options?: NavigateOptions): void;
  /**
   * Prefetch the provided href.
   */
  prefetch(href: string, options?: PrefetchOptions): void;
};

/**
 * `useSafeRouter` is a drop‑in replacement for `useSafeRouter` that applies the
 * same navigation guard used by `<SafeLink />`.
 *
 * ```tsx
 * const router = useSafeRouter();
 * router.push('/about-us');  // internal route - uses Next.js router
 * router.push('/notice');    // external route - uses window.location with properly prefixed URL
 * ```
 */
export const useSafeRouter = (): AppRouterInstance => {
  const router = useRouter();

  const openExternal = (url: string) => window.location.assign(url);

  return {
    ...router,
    push: (href, options) => {
      const { url, isNextRoute } = sanitizeURL(String(href));
      if (!isNextRoute) {
        openExternal(url);
        return;
      }
      router.push(url, options);
    },
    replace: (href, options) => {
      const { url, isNextRoute } = sanitizeURL(String(href));
      if (!isNextRoute) {
        openExternal(url);
        return;
      }
      router.replace(url, options);
    },
    prefetch: (href, options) => {
      const { url, isNextRoute } = sanitizeURL(String(href));
      if (isNextRoute) {
        router.prefetch(url, options);
      }
    },
  } satisfies AppRouterInstance;
};
